import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const raceBibVariants = cva(
  [
    'inline-flex items-center gap-1.5 rounded-[4px]',
    'font-geist-mono font-bold leading-none',
    'border border-fd-text uppercase',
  ],
  {
    variants: {
      tone: {
        race:  'bg-fd-accent text-fd-text',
        teal:  'bg-fd-teal text-fd-bg',
        coral: 'bg-fd-coral text-fd-bg',
        ink:   'bg-fd-text text-fd-bg',
        outline: 'bg-transparent text-fd-text',
      },
      size: {
        sm: 'h-5 px-1.5 text-[10px]',
        md: 'h-6 px-2 text-[11px]',
        lg: 'h-7 px-2.5 text-[13px]',
        xl: 'h-9 px-3 text-[15px]',
      },
      shadow: {
        none: '',
        sm:   'shadow-[2px_2px_0_0_var(--fd-text)]',
        md:   'shadow-[3px_3px_0_0_var(--fd-text)]',
      },
    },
    defaultVariants: { tone: 'race', size: 'md', shadow: 'sm' },
  },
);

export interface RaceBibProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof raceBibVariants> {
  /** The number / letter on the bib (e.g. "01", "04", "00"). */
  n: React.ReactNode;
  /** Optional label after the number (e.g. "COHORT LIVE", "SALES CALLS"). */
  label?: React.ReactNode;
}

/**
 * RaceBib — the brand IP-lock chrome element. A numbered race bib in the brand's
 * accent color, ink-outlined, with optional drop-shadow for the engineered SaaS
 * stamp feel. Used on characters (chest bibs) AND in chrome (cohort markers,
 * stage tags, telemetry plates).
 */
export function RaceBib({ n, label, tone, size, shadow, className, ...rest }: RaceBibProps) {
  return (
    <span className={cn(raceBibVariants({ tone, size, shadow }), className)} {...rest}>
      <b className="font-bold tabular-nums tracking-[-0.02em]">{n}</b>
      {label && <span className="font-semibold tracking-[0.06em] text-[0.85em]">{label}</span>}
    </span>
  );
}

export { raceBibVariants };
